/* eslint-disable max-lines */
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { BlueDotIcon } from '@components/shared/icons.tsx';
import { Duration } from 'luxon';
import AddSpeaker from '@components/editors/transcript-editor/body/add-speaker.tsx';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  MenuButton,
  MenuItems,
  Portal,
  Transition
} from '@headlessui/react';
import classNames from 'classnames';
import cn from 'classnames';
import { useReadOnly } from 'slate-react';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import ChangeAllSpeakers from '@components/editors/transcript-editor/body/change-all-speakers.tsx';
import {
  BrainCircuit,
  CheckCircle2,
  CircleMinus,
  Edit,
  UserPlus,
  UserX,
  Minus,
  BookUser,
  MoveUp,
  MoveDown,
  Scissors,
  X,
  Merge,
  Fingerprint,
  Mic
} from 'lucide-react';
import Modal from '@components/atoms/modals/modal.tsx';
import { TranscriptAddToTrainingPanel } from '@components/editors/transcript-editor/body/transcript-add-to-training-panel.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import toast from 'react-hot-toast';
import {
  useSearchSpeakers,
  useCreateSpeaker,
  useDeleteSpeaker
} from '@utils/api/organizations/speakers';
import { Speaker } from '@utils/types/organization/speaker.ts';
import { useDebouncedValue } from '@utils/hooks/use-debounced-value.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useScrollbarMenuGuard } from '@utils/hooks/use-scrollbar-menu-guard.ts';
import { useRecentSpeakerNames } from '@utils/hooks/use-recent-speaker-names.ts';
import { SpeakerRenameField } from '@components/editors/shared/speaker-rename-field.tsx';
import { useVoiceProfileFeature } from '@components/editors/shared/voice-profiles/use-voice-profile-feature.ts';
import { useVoiceIdentity } from '@components/editors/shared/voice-profiles/use-voice-identity.ts';
import { useVoiceSpeakerRow } from '@components/editors/shared/voice-profiles/use-voice-speaker-row.tsx';

type TranscriptSpeakerProps = {
  speaker: string;
  start: number;
  setSpeaker: (value: string) => void;
  index: number; // row id == index
  replaceSpeakers: (value: string) => void;
};

export const TranscriptSpeaker: React.FC<TranscriptSpeakerProps> = ({
  speaker,
  start,
  setSpeaker,
  index,
  replaceSpeakers
}) => {
  // In split view the source pane's Editable is read-only. The active-row
  // event still reaches us (it's a window broadcast, not editor-scoped)
  // so we'd otherwise render the add/split/delete/merge action row on the
  // source side too. Gate it off — Slate's own `useReadOnly` is the
  // canonical signal here.
  const readOnly = useReadOnly();
  const [showModal, setShowModal] = useState(false);
  const [showAllSpeakersModal, setShowAllSpeakersModal] = useState(false);
  const [showTraining, setShowTraining] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(speaker ?? '');
  const [searchQuery, setSearchQuery] = useState('');

  // NEW: which row is currently active (defaults to none)
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Ref for the rename input to handle auto-selection
  const renameInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const scrollGuardRef = useScrollbarMenuGuard();
  const {
    speakers,
    selectedParagraphs,
    addUtterance,
    splitUtterance,
    deleteUtterance,
    mergeUtterances,
    session,
    documentResult
  } = useTranscript();

  // --- Voice profiles (#2355) -------------------------------------------
  // Identity assignment and voice enrollment hang off the speaker label the
  // user already clicks. Everything is gated: the feature needs the
  // organization's opt-in, enrollment needs an admin, and a read-only pane
  // exposes neither. All rows share one capabilities/timeline/identification
  // query because the keys are identical.
  const voiceSessionId = session?.session_id ?? '';
  const voiceDocumentId = documentResult?.result?.id ?? '';
  const voiceFeature = useVoiceProfileFeature({ session, readOnly });
  // Identity data is fetched up front only where a suggestion could be waiting
  // to be reviewed. Otherwise it waits until the user opens a voice control,
  // so a document in an organization that never uses the feature costs one
  // capabilities request and nothing more.
  const [voiceActivated, setVoiceActivated] = useState(false);
  const voiceIdentity = useVoiceIdentity(
    voiceSessionId,
    voiceDocumentId,
    !!voiceSessionId &&
      !!voiceDocumentId &&
      voiceFeature.canAssign &&
      (voiceFeature.eagerIdentity || voiceActivated)
  );
  const voiceRow = useVoiceSpeakerRow({
    sessionId: voiceSessionId,
    documentId: voiceDocumentId,
    startMs: start ?? 0,
    currentName: speaker ?? '',
    feature: voiceFeature,
    identity: voiceIdentity,
    // Explicit user actions (directory pick, "This voice everywhere",
    // suggestion-chip accept) rename every paragraph of this detected voice
    // via the normal replace-speakers path.
    onApplyName: replaceSpeakers,
    // Auto-apply only touches this paragraph — mis-diarized files that
    // grouped different voices under one label stay rename-correctable.
    onApplyNameToSegment: setSpeaker,
    onActivate: () => setVoiceActivated(true)
  });

  // Debounced search for speaker directory. Two entry points feed it: the
  // popup's top search box (searchQuery) and the inline rename field (tempName).
  // OR-ing them means the directory results — and therefore the rename
  // autocomplete <datalist> — follow whichever the user is typing into.
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const debouncedRename = useDebouncedValue(tempName, 300);

  // Fetch speakers from directory.
  // Limit raised from 10 → 50: with a 10-cap the picker missed any directory
  // speaker past the first 10 alphabetical, since search now runs server-side
  // and the result list isn't re-filtered against the whole directory anymore.
  const { data: directorySpeakers } = useSearchSpeakers({
    search: debouncedSearch || debouncedRename,
    limit: 50
  });

  // Mutation to save new speakers to directory
  const createSpeakerMutation = useCreateSpeaker();
  const deleteSpeakerMutation = useDeleteSpeaker();

  // Per-device recents feed the rename dropdown's "Recently used" section.
  const { remember } = useRecentSpeakerNames();

  // Listen once for the active row event
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ index: number }>).detail;
      setActiveIndex(detail?.index ?? null);
    };
    window.addEventListener('transcript:activeRow', handler as EventListener);
    return () =>
      window.removeEventListener(
        'transcript:activeRow',
        handler as EventListener
      );
  }, []);

  // Auto-select text when entering edit mode
  useEffect(() => {
    if (isEditingName && renameInputRef.current) {
      // Use setTimeout to ensure the input is fully rendered and focused
      setTimeout(() => {
        if (renameInputRef.current) {
          renameInputRef.current.select();
        }
      }, 0);
    }
  }, [isEditingName]);

  const isActiveRow = activeIndex === index;

  // --- sort helpers (unchanged)
  const isNoName = (s: string) =>
    s.trim().toLowerCase() === 'no name' || s.trim() === '';
  const parseNumericDefault = (s: string): number | null => {
    const t = s.trim();
    const m1 = t.match(/^speaker\s*(\d+)$/i);
    if (m1) return Number(m1[1]);
    const m2 = t.match(/^\d+$/);
    if (m2) return Number(t);
    return null;
  };

  // Get directory speakers as full objects (with role/party)
  const directorySpeakersList = useMemo(() => {
    if (!directorySpeakers?.result) return [];
    return directorySpeakers.result;
  }, [directorySpeakers]);

  // Helper: format a directory entry for display ("Name (Role - Party)" or "Name").
  const formatDirEntry = (s: Speaker): string => {
    const parts = [s.role, s.party].filter(Boolean);
    return parts.length ? `${s.name} (${parts.join(' - ')})` : s.name;
  };

  // Resolve a possibly-formatted rename suggestion to its display form.
  // "Sharon Dijksma (Voorzitter)" → "Sharon Dijksma (Voorzitter)" (keeps role).
  // Plain name with no directory match → returned unchanged.
  const resolveToName = (value: string): string => {
    const trimmed = value.trim();
    const dir = directorySpeakersList.find(
      (s: Speaker) => formatDirEntry(s).toLowerCase() === trimmed.toLowerCase()
    );
    return dir ? formatDirEntry(dir) : trimmed;
  };

  // Options for the inline-rename field: local-only speakers (plain names) plus
  // every directory entry as a formatted "Name (Role - Party)" string. This lets
  // the rename dropdown show all three "Sharon Dijksma" entries separately instead
  // of collapsing them to one. resolveToName() preserves the formatted name so
  // the role/party is persisted onto the utterance.
  const renameSuggestions = useMemo(() => {
    const dirNameSet = new Set([
      ...directorySpeakersList.map((s: Speaker) => s.name.trim().toLowerCase()),
      ...directorySpeakersList.map((s: Speaker) =>
        formatDirEntry(s).trim().toLowerCase()
      )
    ]);
    // Local speakers not already covered by a directory entry
    const localOnly = speakers.filter(
      n => !dirNameSet.has(n.trim().toLowerCase())
    );
    // One formatted string per directory entry — keeps same-name/different-role entries distinct
    const dirFormatted = directorySpeakersList.map(formatDirEntry);
    return [...localOnly, ...dirFormatted];
  }, [speakers, directorySpeakersList]);

  // Combine local transcript speakers with directory speakers.
  // Only add directory names when there's a search query (without search we only
  // show speakers already present in this transcript).
  const directorySpeakerNames = useMemo(
    () => directorySpeakersList.map((s: Speaker) => s.name),
    [directorySpeakersList]
  );

  const allSpeakerNames = useMemo(() => {
    const localSpeakers = new Set(speakers);
    if (searchQuery.trim()) {
      const dirNames = new Set(directorySpeakerNames);
      return Array.from(new Set([...localSpeakers, ...dirNames]));
    }
    return Array.from(localSpeakers);
  }, [speakers, directorySpeakerNames, searchQuery]);

  const sortedSpeakers = useMemo(() => {
    const cmp = (a: string, b: string) => {
      // Chairperson always comes first
      const aIsChair = a.trim().toLowerCase() === 'chairperson';
      const bIsChair = b.trim().toLowerCase() === 'chairperson';
      if (aIsChair && !bIsChair) return -1;
      if (!aIsChair && bIsChair) return 1;

      const aNo = isNoName(a);
      const bNo = isNoName(b);
      if (aNo && !bNo) return 1;
      if (!aNo && bNo) return -1;
      if (aNo && bNo) return 0;

      const na = parseNumericDefault(a);
      const nb = parseNumericDefault(b);
      if (na !== null && nb !== null) return na - nb;
      if (na !== null) return -1;
      if (nb !== null) return 1;

      return a.localeCompare(b, undefined, {
        sensitivity: 'base',
        numeric: true
      });
    };
    return [...allSpeakerNames].sort(cmp);
  }, [allSpeakerNames]);

  // Calculate dynamic width based on the longest *rendered* row. Named speakers
  // display as "Name (Role - Party)", so measuring the name alone undershoots.
  // Use the longest display string across all directory entries for a name (not
  // just the first match) so the popup is wide enough for every variant.
  const maxSpeakerLength = useMemo(() => {
    const maxSuffixLen = (name: string): number => {
      const matches = directorySpeakersList.filter(
        (s: Speaker) =>
          s.name.trim().toLowerCase() === name.trim().toLowerCase()
      );
      if (!matches.length) return 0;
      return Math.max(
        ...matches.map((s: Speaker) => {
          const parts = [s.role, s.party].filter(Boolean);
          return parts.length ? ` (${parts.join(' - ')})`.length : 0;
        })
      );
    };
    const allNames = [speaker, ...allSpeakerNames];
    return Math.max(
      ...allNames.map(s => (s || '').length + maxSuffixLen(s || ''))
    );
  }, [speaker, allSpeakerNames, directorySpeakersList]);

  // Filter speakers based on search query
  const filteredSpeakers = useMemo(() => {
    if (!searchQuery.trim()) return sortedSpeakers;
    const query = searchQuery.toLowerCase();
    return sortedSpeakers.filter(s => s.toLowerCase().includes(query));
  }, [sortedSpeakers, searchQuery]);

  // Helper to check if a name looks like a generic speaker pattern
  const isGenericSpeakerName = (name: string): boolean => {
    const trimmed = name.trim().toLowerCase();
    if (/^speaker\s*\d*$/i.test(trimmed)) return true;
    if (/^\d+$/.test(trimmed)) return true;
    if (trimmed === 'no name' || trimmed === '') return true;
    return false;
  };

  const isChairperson = (name: string) =>
    name.trim().toLowerCase() === 'chairperson';

  // Split the filtered list into three buckets so we can render them as
  // labelled groups: chairperson pinned on top, generic / unassigned speakers
  // grouped separately right after, then named directory speakers below.
  const groupedSpeakers = useMemo(() => {
    const chair: string[] = [];
    const generic: string[] = [];
    const named: string[] = [];
    for (const s of filteredSpeakers) {
      if (isChairperson(s)) chair.push(s);
      else if (isGenericSpeakerName(s)) generic.push(s);
      else named.push(s);
    }
    return { chair, generic, named };
  }, [filteredSpeakers]);

  // All directory entries that should appear in the NAMED SPEAKERS group — one
  // row per entry, not deduplicated by name, so three "Sharon Dijksma" entries
  // with different roles each get their own row.
  const namedDirectoryEntries = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.trim().toLowerCase();
    return directorySpeakersList.filter((s: Speaker) => {
      const name = s.name.trim();
      if (!name || name.toLowerCase() === 'no name') return false;
      if (/^speaker\s*\d*$/i.test(name.toLowerCase()) || /^\d+$/.test(name))
        return false;
      if (name.toLowerCase() === 'chairperson') return false;
      return [name, s.role ?? '', s.party ?? '']
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [directorySpeakersList, searchQuery]);

  // Local named speakers not covered by any directory entry; these appear below
  // the directory rows in the NAMED section.
  const localOnlyNamed = useMemo(() => {
    if (!searchQuery.trim() || !directorySpeakersList.length)
      return [] as string[];
    const dirNameSet = new Set([
      ...directorySpeakersList.map((s: Speaker) => s.name.trim().toLowerCase()),
      ...directorySpeakersList.map((s: Speaker) =>
        formatDirEntry(s).trim().toLowerCase()
      )
    ]);
    return groupedSpeakers.named.filter(
      n => !dirNameSet.has(n.trim().toLowerCase())
    );
  }, [groupedSpeakers.named, directorySpeakersList, searchQuery]);

  // Save new speaker to directory. Fire-and-forget: BE is idempotent
  // (api-scriptix #3785), so a duplicate name returns the existing row instead
  // of erroring. Any genuine failure (network, auth) is swallowed because the
  // rename has already happened locally and the directory write is best-effort.
  //
  // When the write actually inserted a new row (created === true), surface an
  // "Undo" toast so an accidental/typo name can be pulled back out of the shared
  // list in one click — directory auto-add is convenient but easy to pollute.
  const saveToDirectory = async (name: string) => {
    if (isGenericSpeakerName(name)) return;
    try {
      const res = await createSpeakerMutation.mutateAsync({ name });
      if (!res.created) return;
      const speakerId = res.speaker.result.id;
      toast.success(
        tst => (
          <span className="flex items-center gap-3">
            {t('added-to-speaker-list', { name })}
            <button
              type="button"
              onClick={() => {
                deleteSpeakerMutation.mutate(speakerId);
                toast.dismiss(tst.id);
              }}
              className="cursor-pointer font-semibold underline"
            >
              {t('undo')}
            </button>
          </span>
        ),
        { duration: 12000 }
      );
    } catch {
      // Best-effort: rename has already taken effect locally; directory write
      // failures shouldn't interrupt the user.
    }
  };

  const submitInlineRename = (
    close: () => void,
    overrideName?: string,
    options: { scope?: 'segment' | 'voice' } = {}
  ) => {
    // Guard against double submission: clicking Save blurs the input (onSubmit)
    // and also triggers the form submit; the first call flips isEditingName off,
    // so the second is a no-op and we don't create the directory speaker twice.
    if (!isEditingName) return;
    const oldName = (speaker || '').trim();
    // Resolve a formatted directory suggestion like "Sharon Dijksma (Voorzitter)"
    // back to just the speaker name "Sharon Dijksma" before committing.
    const rawNext = (overrideName ?? tempName ?? '').trim();
    const next = resolveToName(rawNext);
    if (!next) {
      setIsEditingName(false);
      setTimeout(close, 0);
      return;
    }
    if (oldName.toLowerCase() === next.toLowerCase()) {
      setIsEditingName(false);
      setTimeout(close, 0);
      return;
    }
    const existing = allSpeakerNames.find(
      s => s.trim().toLowerCase() === next.toLowerCase()
    );
    const target = existing ?? next;
    const dirSpeaker = getDirectorySpeaker(next);
    const fromDir = !!dirSpeaker;

    // Default: just this paragraph. Users split a mis-diarized paragraph
    // precisely to give each piece a different speaker — the pre-existing
    // multi-row default made that workflow impossible. The "voice" scope is
    // still available via the secondary button for well-diarized files.
    const scope = options.scope ?? 'segment';

    if (scope === 'voice') {
      // "This voice everywhere" is the one explicit voice-wide action. A
      // rename that resolves to a directory person also records the
      // voice-to-person link on the backend in the same step.
      const canUnifyWithIdentity =
        fromDir &&
        voiceRow.canAssign &&
        !voiceRow.unavailableReason &&
        !!voiceRow.speakerLabel;

      if (canUnifyWithIdentity && dirSpeaker) {
        void voiceRow.applyPerson(dirSpeaker.id, target, { scope: 'voice' });
      } else {
        replaceSpeakers(target);
      }
    } else {
      // Segment scope: no identity link, no propagation. Just this row.
      setSpeaker(target);
    }

    // Remember non-generic names for this device's "Recently used" section.
    if (!isGenericSpeakerName(target)) remember(target);

    // Save to directory only if it's truly new — not a directory entry that was
    // already formatted (e.g. "Sharon Dijksma (Voorzitter)").
    if (!existing && !isGenericSpeakerName(next) && !fromDir) {
      saveToDirectory(next);
    }

    setIsEditingName(false);
    setTimeout(close, 0);
  };

  const actionItems = (close: () => void) => [
    {
      id: -1,
      icon: <UserPlus width={16} className="text-blue-500" />,
      text: t('add-speaker'),
      onClick: () => {
        setShowModal(true);
        close();
      }
    },
    {
      id: -2,
      icon: <UserX width={16} className="text-red-500" />,
      text: t('no-speaker'),
      onClick: () => {
        setSpeaker('');
        close();
      }
    },
    {
      id: -4,
      icon: <Minus width={16} className="text-gray-500" />,
      text: t('no-name') || 'No name',
      onClick: () => {
        setSpeaker('No name');
        close();
      }
    },
    // This document's source has no speaker separation, so no identity
    // decision can be keyed to anything. Say so rather than silently
    // offering nothing — the user would otherwise have no way to tell the
    // feature apart from a missing permission.
    ...(voiceRow.canAssign && voiceRow.unavailableReason === 'no-diarization'
      ? [
          {
            id: -5,
            icon: <Fingerprint width={16} className="text-gray-500" />,
            text: t('voice-error-no-diarization'),
            disabled: true,
            onClick: () => {}
          }
        ]
      : []),
    // Explicit "unknown voice" decision — kept as its own action so an
    // ambiguous speaker can be marked without clearing the row's name, and
    // the recorded decision survives reruns the same way a named choice does.
    ...(voiceRow.canAssign &&
    !voiceRow.unavailableReason &&
    !!voiceRow.speakerLabel
      ? [
          {
            id: -6,
            icon: <Fingerprint width={16} className="text-gray-500" />,
            text: t('voice-mark-unknown'),
            onClick: () => {
              // Mark unknown is intentionally voice-wide: a declaration
              // about the backend identity for this detected voice.
              void voiceRow.applyPerson(null, undefined, { scope: 'voice' });
              close();
            }
          }
        ]
      : []),
    // Admins can jump to the Save-voice flow directly from the menu. When no
    // person is linked yet, the picker runs first and the chosen person flows
    // straight into enrollment — never a dead-end "pick a person then stop".
    ...(voiceRow.canEnroll && !voiceRow.unavailableReason
      ? [
          {
            id: -7,
            icon: <Mic width={16} className="text-blue-500" />,
            text: t('voice-save-sample'),
            onClick: () => {
              voiceRow.openEnroll();
              close();
            }
          }
        ]
      : [])
  ];

  // Get full speaker object from directory by name — matches both bare name and
  // formatted "Name (Role - Party)" so lookups work after formatted names are stored.
  const getDirectorySpeaker = (name: string): Speaker | undefined => {
    const lower = name.trim().toLowerCase();
    return directorySpeakersList.find(
      (s: Speaker) =>
        s.name.trim().toLowerCase() === lower ||
        formatDirEntry(s).toLowerCase() === lower
    );
  };

  // Check if speaker is from directory
  const isFromDirectory = (name: string): boolean => {
    return !!getDirectorySpeaker(name);
  };

  // Format speaker display with role/party if available. Using formatDirEntry
  // avoids double-formatting when the stored name is already "Name (Role - Party)".
  const formatSpeakerDisplay = (name: string): string => {
    const dirSpeaker = getDirectorySpeaker(name);
    if (!dirSpeaker) return name;
    return formatDirEntry(dirSpeaker);
  };

  const mapSpeakerItems = (list: string[], close: () => void) =>
    list.map((item, idx) => ({
      id: idx,
      icon:
        item === speaker ? (
          <CheckCircle2 width={16} />
        ) : (
          <CircleMinus width={16} />
        ),
      text: item,
      displayText: formatSpeakerDisplay(item),
      fromDirectory: isFromDirectory(item),
      onClick: () => {
        setSpeaker(item);
        close();
      }
    }));

  return (
    <div
      className={cn('flex justify-between h-12 w-full')}
      contentEditable={false}
      onClick={e => {
        e.stopPropagation();
        if (window.getSelection) window.getSelection()?.removeAllRanges();
      }}
    >
      {/* LEFT: Speaker picker + timestamp */}
      <div className="items-center flex align-middle text-[15px] font-semibold max-w-fit">
        <span
          className="inline-flex mr-2 items-center gap-x-1 rounded-full px-2 py-1 text-sm font-medium ring-1 ring-inset ring-blue-200 dark:ring-zinc-700"
          style={{
            maxWidth: `min(${Math.min(maxSpeakerLength + 10, 100)}ch, 85vw)`
          }}
          title={formatSpeakerDisplay(speaker)}
        >
          <BlueDotIcon className="h-1.5 w-1.5 fill-blue-500 dark:fill-blue-400/60" />

          <Menu as="div" className="relative">
            {({ close }) => (
              <>
                <MenuButton
                  className="flex text-start gap-x-2"
                  aria-label={`${t('speaker')}: ${formatSpeakerDisplay(speaker)}. ${t('change-speaker') || 'Change speaker'}`}
                  onClick={() => {
                    // Kick off identity fetching the moment the picker opens.
                    // Without this, on an org whose recognition thresholds are
                    // not calibrated, `eagerIdentity` stays false and the
                    // timeline is never fetched — so a directory-person pick
                    // can't record the voice-to-person link and only updates
                    // the clicked row. One flip is enough; the hook
                    // deduplicates further activation.
                    if (!voiceActivated) setVoiceActivated(true);
                  }}
                >
                  <input
                    className={cn(
                      'min-w-[6rem] w-auto outline-none border-none min-h-5 cursor-pointer'
                    )}
                    style={{
                      maxWidth: `min(${Math.min(maxSpeakerLength + 2, 100)}ch, 90vw)`
                    }}
                    value={formatSpeakerDisplay(speaker)}
                    title={formatSpeakerDisplay(speaker)}
                    aria-label={t('speaker') || 'Speaker'}
                    tabIndex={-1}
                    readOnly
                  />
                </MenuButton>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Portal>
                    <MenuItems
                      anchor="bottom start"
                      style={{
                        width: `min(${Math.min(maxSpeakerLength + 10, 120)}ch, 95vw)`,
                        minWidth: '20rem',
                        backgroundColor: getColour('thirdLayer'),
                        borderColor: getColour('border'),
                        color: getColour('normalText')
                      }}
                      className={cn(
                        'z-[999] mt-2.5 origin-top-left',
                        'rounded-xl border backdrop-blur',
                        'shadow-lg p-1'
                      )}
                    >
                      {/* Search Input - Sticky at top */}
                      <div
                        className="sticky top-0 z-10 p-2 border-b"
                        style={{
                          backgroundColor: getColour('thirdLayer'),
                          borderColor: getColour('hairline')
                        }}
                      >
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder={
                            t('search-speakers') || 'Search speakers...'
                          }
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => {
                            e.stopPropagation();
                            if (e.key === 'Escape') {
                              setSearchQuery('');
                              searchInputRef.current?.blur();
                            }
                          }}
                          style={{
                            backgroundColor: getColour('secondLayer'),
                            borderColor: getColour('border'),
                            color: getColour('normalText')
                          }}
                          className="w-full rounded-2xl border px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/40"
                        />
                        {/* The ticket requires "explain that this applies to
                            that detected voice across the recording." The
                            inline picker is the primary surface, so the hint
                            lives here — right where the user is about to
                            click a directory person. */}
                        {voiceRow.canAssign &&
                          !voiceRow.unavailableReason &&
                          !!voiceRow.speakerLabel && (
                            <p
                              className="mt-1.5 px-1 text-[11px] leading-snug"
                              style={{ color: getColour('inactiveText') }}
                            >
                              {t('voice-picker-scope-hint')}
                            </p>
                          )}
                      </div>

                      <div
                        ref={scrollGuardRef}
                        className="overflow-y-auto overscroll-contain"
                        style={{ maxHeight: '280px' }}
                      >
                        {/* 1st: Rename Section */}
                        <div
                          className="flex w-full items-center gap-2 rounded-2xl px-3 py-2 text-left text-sm leading-5 hover:bg-primary/5"
                          onClick={e => e.stopPropagation()}
                          onKeyDownCapture={e => e.stopPropagation()}
                          tabIndex={-1}
                        >
                          {!isEditingName ? (
                            <button
                              type="button"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => {
                                setTempName(speaker ?? '');
                                setIsEditingName(true);
                              }}
                              className="flex items-center gap-2 w-full"
                            >
                              <Edit
                                width={16}
                                className="shrink-0"
                                style={{ color: getColour('inactiveText') }}
                              />
                              <span className="break-words flex-1 text-start">
                                {t('rename-this-speaker')}
                              </span>
                            </button>
                          ) : (
                            <form
                              className="flex flex-wrap items-center gap-2 w-full"
                              onSubmit={e => {
                                e.preventDefault();
                                submitInlineRename(close);
                              }}
                              onKeyDownCapture={e => e.stopPropagation()}
                            >
                              <SpeakerRenameField
                                ref={renameInputRef}
                                name="transcript-speaker-name"
                                value={tempName}
                                onChange={setTempName}
                                onSubmit={() => submitInlineRename(close)}
                                onSelect={selected =>
                                  submitInlineRename(close, selected)
                                }
                                onCancel={() => {
                                  setIsEditingName(false);
                                  setTimeout(close, 0);
                                }}
                                listSuggestions={renameSuggestions}
                                placeholder={t('enter-new-name')}
                              />
                              {/* Primary Save = this paragraph only. Users
                                  split a mis-diarized paragraph expecting
                                  each piece to be independent; renaming one
                                  row never surprises the others. The
                                  secondary button opts into the session-wide
                                  rename when the whole voice really is this
                                  person — picking from the directory list
                                  below records that identity link too. */}
                              <button
                                type="submit"
                                style={{
                                  borderColor: getColour('border'),
                                  color: getColour('normalText')
                                }}
                                className="rounded-2xl px-2 py-1 text-xs font-medium border hover:bg-primary/5"
                                title={t('rename-this-segment-only-hint')}
                              >
                                {t('save')}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  submitInlineRename(close, undefined, {
                                    scope: 'voice'
                                  })
                                }
                                style={{
                                  borderColor: getColour('border'),
                                  color: getColour('inactiveText')
                                }}
                                className="rounded-2xl px-2 py-1 text-xs font-medium border hover:bg-primary/5"
                                title={t('rename-this-voice-scope-hint')}
                              >
                                {t('rename-this-voice-everywhere')}
                              </button>
                            </form>
                          )}
                        </div>

                        {/* 2nd: Action Items (Add/Remove/No name) */}
                        {actionItems(close).map(item => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={item.onClick}
                            disabled={'disabled' in item && item.disabled}
                            className={classNames(
                              'flex w-full items-center gap-1 rounded-2xl px-3 py-1 text-left text-sm leading-5 transition hover:bg-primary/5',
                              'disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent'
                            )}
                          >
                            <span className="shrink-0">{item.icon}</span>
                            <span className="break-words flex-1">
                              {item.text}
                            </span>
                          </button>
                        ))}

                        <div
                          className="my-1 h-px"
                          style={{ backgroundColor: getColour('hairline') }}
                        />

                        {/* 3rd: Speaker List — grouped */}
                        {(() => {
                          const chairItems = mapSpeakerItems(
                            groupedSpeakers.chair,
                            close
                          );
                          const genericItems = mapSpeakerItems(
                            groupedSpeakers.generic,
                            close
                          );
                          const totalCount =
                            chairItems.length +
                            genericItems.length +
                            namedDirectoryEntries.length +
                            localOnlyNamed.length;

                          if (totalCount === 0) {
                            return (
                              searchQuery && (
                                <div
                                  className="px-3 py-4 text-center text-sm"
                                  style={{ color: getColour('inactiveText') }}
                                >
                                  {t('no-speakers-found') ||
                                    'No speakers found'}
                                </div>
                              )
                            );
                          }

                          const renderRow = (
                            item: ReturnType<typeof mapSpeakerItems>[number]
                          ) => {
                            const isActive = item.text === speaker;
                            return (
                              <button
                                key={`spk-${item.text}`}
                                type="button"
                                onClick={item.onClick}
                                className="flex w-full items-center gap-1 rounded-2xl px-3 py-1 text-left text-sm leading-5 transition hover:bg-primary/5"
                              >
                                <span className="shrink-0">
                                  {isActive ? (
                                    <CheckCircle2 width={16} />
                                  ) : (
                                    <CircleMinus width={16} />
                                  )}
                                </span>
                                <span
                                  className={cn(
                                    'break-words flex-1',
                                    isChairperson(item.text) && 'font-bold'
                                  )}
                                >
                                  {item.displayText}
                                </span>
                                {item.fromDirectory && (
                                  <span
                                    className="shrink-0 text-blue-500"
                                    title={
                                      t('from-speaker-directory') ||
                                      'From speaker directory'
                                    }
                                  >
                                    <BookUser width={12} />
                                  </span>
                                )}
                                {isActive && (
                                  <span className="ml-auto opacity-80 shrink-0">
                                    <CheckCircle2 width={14} />
                                  </span>
                                )}
                              </button>
                            );
                          };

                          const groupLabel = (
                            label: string,
                            withTopDivider: boolean
                          ) => (
                            <>
                              {withTopDivider && (
                                <div
                                  className="my-1 h-px"
                                  style={{
                                    backgroundColor: getColour('hairline')
                                  }}
                                />
                              )}
                              <div
                                className="px-3 pt-1 pb-0.5 text-xs font-semibold uppercase tracking-wide"
                                style={{ color: getColour('inactiveText') }}
                              >
                                {label}
                              </div>
                            </>
                          );

                          return (
                            <>
                              {chairItems.length > 0 && (
                                <>
                                  {groupLabel(
                                    t('chairperson') || 'Chairperson',
                                    false
                                  )}
                                  {chairItems.map(renderRow)}
                                </>
                              )}
                              {genericItems.length > 0 && (
                                <>
                                  {groupLabel(
                                    t('unassigned-speakers') ||
                                      'Unassigned speakers',
                                    chairItems.length > 0
                                  )}
                                  {genericItems.map(renderRow)}
                                </>
                              )}
                              {(namedDirectoryEntries.length > 0 ||
                                localOnlyNamed.length > 0) && (
                                <>
                                  {groupLabel(
                                    t('named-speakers') || 'Named speakers',
                                    chairItems.length > 0 ||
                                      genericItems.length > 0
                                  )}
                                  {namedDirectoryEntries.map((s: Speaker) => {
                                    const speakerLower = (speaker || '')
                                      .trim()
                                      .toLowerCase();
                                    const isActive =
                                      formatDirEntry(s).toLowerCase() ===
                                        speakerLower ||
                                      s.name.trim().toLowerCase() ===
                                        speakerLower;
                                    const display = formatDirEntry(s);
                                    const canUnify =
                                      voiceRow.canAssign &&
                                      !voiceRow.unavailableReason &&
                                      !!voiceRow.speakerLabel;
                                    return (
                                      <button
                                        key={`dir-${s.id}`}
                                        type="button"
                                        onClick={() => {
                                          // Record the voice-to-person link
                                          // session-wide on the backend, but
                                          // only rename THIS paragraph locally
                                          // — splitting a mis-diarized
                                          // paragraph means siblings may be
                                          // different voices, and a surprise
                                          // multi-row rename is the exact
                                          // complaint. For well-diarized
                                          // files the user can propagate the
                                          // name explicitly via the rename
                                          // field's "This voice everywhere"
                                          // button.
                                          if (canUnify) {
                                            void voiceRow.applyPerson(
                                              s.id,
                                              display,
                                              { scope: 'segment' }
                                            );
                                          } else {
                                            setSpeaker(display);
                                          }
                                          close();
                                        }}
                                        className="flex w-full items-center gap-1 rounded-2xl px-3 py-1 text-left text-sm leading-5 transition hover:bg-primary/5"
                                      >
                                        <span className="shrink-0">
                                          {isActive ? (
                                            <CheckCircle2 width={16} />
                                          ) : (
                                            <CircleMinus width={16} />
                                          )}
                                        </span>
                                        <span className="break-words flex-1">
                                          {display}
                                        </span>
                                        <span
                                          className="shrink-0 text-blue-500"
                                          title={
                                            t('from-speaker-directory') ||
                                            'From speaker directory'
                                          }
                                        >
                                          <BookUser width={12} />
                                        </span>
                                      </button>
                                    );
                                  })}
                                  {localOnlyNamed.map((name: string) => {
                                    const isActive = name === speaker;
                                    return renderRow({
                                      id: 0,
                                      icon: isActive ? (
                                        <CheckCircle2 width={16} />
                                      ) : (
                                        <CircleMinus width={16} />
                                      ),
                                      text: name,
                                      displayText: name,
                                      fromDirectory: false,
                                      onClick: () => {
                                        setSpeaker(name);
                                        close();
                                      }
                                    });
                                  })}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </MenuItems>
                  </Portal>
                </Transition>
              </>
            )}
          </Menu>
        </span>

        <span>{`${Duration.fromMillis(start ? start : 0).toFormat('hh:mm:ss')}`}</span>

        {/* A reviewable name suggestion, or the recorded decision, beside the
            label. Nothing is applied until the editor accepts it. */}
        {voiceRow.chip && <span className="ml-2">{voiceRow.chip}</span>}
      </div>

      {/* RIGHT: action buttons — visible only on the active row and
          only when the editor is writable (hidden in the split-view
          source pane). */}
      {isActiveRow &&
        !readOnly &&
        (() => {
          const btnClass =
            'h-8 w-8 flex items-center justify-center rounded-full border transition-colors hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed';
          const btnStyle = {
            borderColor: getColour('border'),
            backgroundColor: getColour('thirdLayer'),
            color: getColour('icon')
          };
          const stop = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
          };
          const canMerge =
            selectedParagraphs.length >= 2 &&
            selectedParagraphs[selectedParagraphs.length - 1] === index;

          return (
            <div className="flex w-full justify-end h-12">
              <div className="flex items-center gap-1">
                {index === 0 && (
                  <Tooltip label={t('add-paragraph')}>
                    <button
                      type="button"
                      onMouseDown={stop}
                      onClick={e => {
                        e.preventDefault();
                        addUtterance(true);
                      }}
                      aria-label={t('add-paragraph')}
                      className={btnClass}
                      style={btnStyle}
                    >
                      <MoveUp size={14} />
                    </button>
                  </Tooltip>
                )}
                <Tooltip label={t('add-paragraph')}>
                  <button
                    type="button"
                    onMouseDown={stop}
                    onClick={e => {
                      e.preventDefault();
                      addUtterance();
                    }}
                    aria-label={t('add-paragraph')}
                    className={btnClass}
                    style={btnStyle}
                  >
                    <MoveDown size={14} />
                  </button>
                </Tooltip>
                <Tooltip label={t('split-paragraph')}>
                  <button
                    type="button"
                    onMouseDown={stop}
                    onClick={e => {
                      stop(e);
                      splitUtterance(index);
                    }}
                    aria-label={t('split-paragraph')}
                    className={btnClass}
                    style={btnStyle}
                  >
                    <Scissors size={14} />
                  </button>
                </Tooltip>
                <Tooltip label={t('delete-paragraph')}>
                  <button
                    type="button"
                    onMouseDown={stop}
                    onClick={e => {
                      e.preventDefault();
                      deleteUtterance(index);
                    }}
                    aria-label={t('delete-paragraph')}
                    className={btnClass}
                    style={btnStyle}
                  >
                    <X size={14} />
                  </button>
                </Tooltip>
                {canMerge && (
                  <Tooltip label={t('merge-paragraphs')}>
                    <button
                      type="button"
                      onMouseDown={stop}
                      onClick={e => {
                        stop(e);
                        mergeUtterances(selectedParagraphs);
                      }}
                      aria-label={t('merge-paragraphs')}
                      className={btnClass}
                      style={btnStyle}
                    >
                      <Merge size={14} />
                    </button>
                  </Tooltip>
                )}
                <Tooltip label={t('add-to-training-data')}>
                  <button
                    type="button"
                    onMouseDown={stop}
                    onClick={e => {
                      stop(e);
                      setShowTraining(true);
                    }}
                    aria-label={t('add-to-training-data')}
                    className={btnClass}
                    style={btnStyle}
                  >
                    <BrainCircuit size={14} />
                  </button>
                </Tooltip>
              </div>
            </div>
          );
        })()}

      {voiceRow.dialogs}

      <AddSpeaker
        show={showModal}
        onClose={() => setShowModal(false)}
        setSpeaker={setSpeaker}
      />
      <ChangeAllSpeakers
        show={showAllSpeakersModal}
        onClose={() => setShowAllSpeakersModal(false)}
        replaceSpeakers={replaceSpeakers}
      />
      <Modal
        isOpen={showTraining}
        onClose={() => setShowTraining(false)}
        title={t('add-to-training-data')}
        size="medium"
        customWidth="min(440px, 100%)"
        customMaxHeight="90vh"
      >
        <TranscriptAddToTrainingPanel
          utteranceIndex={index}
          onClose={() => setShowTraining(false)}
        />
      </Modal>
    </div>
  );
};
